import { useState, useEffect } from 'react';

interface SimpleAnimatedNumberProps {
  value: number;
  prefix?: string;
  suffix?: string;
  direction?: 'up' | 'down';
  startValue?: number;
  durationMs?: number;
  endTimeMs?: number; // New prop for synchronized end time
  className?: string;
  delayMs?: number;
}

export const SimpleAnimatedNumber = ({
  value,
  prefix = '',
  suffix = '',
  direction = 'up',
  startValue,
  durationMs = 1100,
  endTimeMs,
  className = '',
  delayMs = 100,
}: SimpleAnimatedNumberProps) => {
  const [count, setCount] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  // Calculate duration based on end time if provided
  const actualDurationMs = endTimeMs ? endTimeMs - delayMs : durationMs;

  useEffect(() => {
    // Start animation after delay
    const timer = setTimeout(() => {
      setIsAnimating(true);
      
      const calculatedStartValue = startValue ?? (direction === 'down' ? Math.max(value + 8, 10) : 0);
      let current = calculatedStartValue;
      
      // Calculate interval based on total duration and value range
      const totalSteps = Math.abs(value - calculatedStartValue);
      const intervalMs = actualDurationMs / totalSteps;
      
      const interval = setInterval(() => {
        if (direction === 'up') {
          current += 1;
          if (current >= value) {
            setCount(value);
            setIsAnimating(false);
            clearInterval(interval);
          } else {
            setCount(current);
          }
        } else {
          current -= 1;
          if (current <= value) {
            setCount(value);
            setIsAnimating(false);
            clearInterval(interval);
          } else {
            setCount(current);
          }
        }
      }, Math.max(intervalMs, 50)); // Minimum 50ms for visibility
    }, delayMs);

    return () => clearTimeout(timer);
  }, [value, direction, startValue, delayMs, actualDurationMs]);

  return (
    <span 
      className={className} 
      style={{ 
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      {prefix}{count}{suffix}
    </span>
  );
};
